import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

const NEW_DEALMACHINE_SIGN_IN_URL = "https://dealmachine.com/sign-in";
const NEW_DEALMACHINE_GUIDE_URL =
  "https://dealmachine.com/guides/classic-account";
const NEW_DEALMACHINE_BANNER_DISMISSED_KEY =
  "newDealMachineBannerDismissed";

export const getNewDealMachineSignInUrl = placement => {
  const utmContent = {
    app: "in_app_banner",
    guide: "migration_guide_cta",
    login: "login_banner"
  }[placement];

  const params = new URLSearchParams({
    source: "classic",
    utm_source: "dealmachine_classic",
    utm_medium: "product",
    utm_campaign: "new_dealmachine_migration",
    utm_content: utmContent || "classic_product_link"
  });

  return `${NEW_DEALMACHINE_SIGN_IN_URL}?${params.toString()}`;
};

export const getNewDealMachineGuideUrl = placement => {
  const params = new URLSearchParams({
    source: "classic",
    utm_source: "dealmachine_classic",
    utm_medium: "product",
    utm_campaign: "new_dealmachine_migration",
    utm_content: `${placement || "app"}_banner_guide`
  });

  return `${NEW_DEALMACHINE_GUIDE_URL}?${params.toString()}`;
};

const clearBannerOffset = () => {
  document.documentElement.style.removeProperty("--new-dm-banner-offset");
  document.body.classList.remove("new-dm-banner-visible");
};

const setBannerOffset = height => {
  document.documentElement.style.setProperty(
    "--new-dm-banner-offset",
    `${height}px`
  );
  document.body.classList.add("new-dm-banner-visible");
};

const getInitialDismissed = () => {
  try {
    return window.localStorage.getItem(NEW_DEALMACHINE_BANNER_DISMISSED_KEY) ===
      "true";
  } catch (e) {
    return false;
  }
};

const NewDealMachineBanner = ({ placement }) => {
  const [dismissed, setDismissed] = useState(getInitialDismissed);
  const bannerRef = useRef(null);
  const bannerClassName = `new-dm-banner new-dm-banner--${placement}`;

  useEffect(() => {
    if (dismissed) {
      clearBannerOffset();
      return;
    }

    const el = bannerRef.current;
    if (!el) {
      return;
    }

    const updateOffset = () => {
      setBannerOffset(el.getBoundingClientRect().height);
    };

    updateOffset();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateOffset);
      observer.observe(el);
    } else {
      window.addEventListener("resize", updateOffset);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener("resize", updateOffset);
      }
      clearBannerOffset();
    };
  }, [dismissed]);

  const dismissBanner = () => {
    try {
      window.localStorage.setItem(NEW_DEALMACHINE_BANNER_DISMISSED_KEY, "true");
    } catch (e) {
      // Ignore storage failures and still hide for this session.
    }
    setDismissed(true);
  };

  if (dismissed) {
    return null;
  }

  return (
    <aside
      ref={bannerRef}
      className={bannerClassName}
      aria-label="Try the new DealMachine"
    >
      <span
        aria-hidden="true"
        className="new-dm-banner__glow new-dm-banner__glow--left"
      />
      <span
        aria-hidden="true"
        className="new-dm-banner__glow new-dm-banner__glow--right"
      />
      <span
        aria-hidden="true"
        className="new-dm-banner__glow new-dm-banner__glow--center"
      />
      <div className="new-dm-banner__pattern" aria-hidden="true" />

      <span className="new-dm-banner__badge">
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3Z" />
          <path d="M5 3v4" />
          <path d="M19 17v4" />
          <path d="M3 5h4" />
          <path d="M17 19h4" />
        </svg>
        New
      </span>

      <span className="new-dm-banner__headline new-dm-banner__headline--desktop">
        Try out the new DealMachine
      </span>
      <span className="new-dm-banner__headline new-dm-banner__headline--mobile">
        New DealMachine
      </span>

      <a
        className="new-dm-banner__primary-link"
        href={getNewDealMachineSignInUrl(placement)}
      >
        Log in
        <span aria-hidden="true">&rarr;</span>
      </a>

      <a
        className="new-dm-banner__guide-link"
        href={getNewDealMachineGuideUrl(placement)}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.1 9a3 3 0 1 1 5.83 1c0 2-3 2-3 4" />
          <path d="M12 18h.01" />
        </svg>
        How it works
      </a>

      <button
        type="button"
        className="new-dm-banner__dismiss"
        onClick={dismissBanner}
        aria-label="Dismiss New DealMachine announcement"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>
    </aside>
  );
};

NewDealMachineBanner.propTypes = {
  placement: PropTypes.oneOf(["app", "login"])
};

NewDealMachineBanner.defaultProps = {
  placement: "app"
};

export default NewDealMachineBanner;
